// Global Instructions Rule Applied! - March 31, 2026
import { useCallback, useEffect, useMemo, useRef } from 'react'
import { DndProvider } from 'react-dnd'
import { HTML5Backend } from 'react-dnd-html5-backend'
import { useDrag, useDrop } from 'react-dnd'
import { Radio, Select, Space } from 'antd'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faGripVertical, faPlus, faTrash } from '@fortawesome/free-solid-svg-icons'
import Button from '../Button.jsx'
import type { ViewConfigColumn } from './types.ts'

const SORT_ROW_TYPE = 'view_config_sort_row'

function isSortableColumn(col: ViewConfigColumn): boolean {
  return col.sortable === true
}

/**
 * Stable identity for an active-sort row's `.key` field — what the frontend
 * sends to the backend.
 *
 * The backend (`resolve_grid_sorting` in
 * `app_modules/reporting/related_grid_enrichment.py`) expects `field_name`
 * here and translates to canonical `cfg.reporting_columns.sort_key` for the
 * RPC's ORDER BY. Sending `sort_key` directly bypasses that translation and
 * the RPC silently no-ops. Therefore this returns `valueKey` (= cfg
 * `field_name`) as the canonical wire-form key.
 */
export function sortColumnKey(col: ViewConfigColumn): string {
  return col.valueKey
}


export interface SortPanelProps {
  columns: ViewConfigColumn[]
  value: SortDraftRow[]
  onChange: (next: SortDraftRow[]) => void
}

export interface SortDraftRow {
  id: string
  valueKey?: string
  direction: 'asc' | 'desc'
}

let sortRowId = 0
export function createSortDraftRow(seed?: Partial<SortDraftRow>): SortDraftRow {
  sortRowId += 1
  return {
    id: `sort-row-${sortRowId}-${Date.now()}`,
    valueKey: seed?.valueKey,
    direction: seed?.direction ?? 'asc'
  }
}

interface DraggableSortRowProps {
  row: SortDraftRow
  index: number
  rowCount: number
  moveSortRow: (dragIndex: number, hoverIndex: number) => void
  optionsForRow: (rowId: string) => { value: string; label: string }[]
  updateRow: (id: string, patch: Partial<SortDraftRow>) => void
  removeRow: (id: string) => void
}

function DraggableSortRow({
  row,
  index,
  rowCount,
  moveSortRow,
  optionsForRow,
  updateRow,
  removeRow
}: DraggableSortRowProps) {
  const rowRef = useRef<HTMLDivElement>(null)
  const gripRef = useRef<HTMLSpanElement>(null)

  const [{ isDragging }, drag] = useDrag({
    type: SORT_ROW_TYPE,
    item: { index, id: row.id },
    collect: (monitor) => ({
      isDragging: monitor.isDragging()
    })
  })

  const [, drop] = useDrop({
    accept: SORT_ROW_TYPE,
    hover: (draggedItem: { index: number; id: string }, monitor) => {
      if (!rowRef.current) return
      const dragIndex = draggedItem.index
      const hoverIndex = index
      if (dragIndex === hoverIndex) return

      const hoverBoundingRect = rowRef.current.getBoundingClientRect()
      const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2
      const clientOffset = monitor.getClientOffset()
      if (!clientOffset) return
      const hoverClientY = clientOffset.y - hoverBoundingRect.top

      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) return
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) return

      moveSortRow(dragIndex, hoverIndex)
      draggedItem.index = hoverIndex
    }
  })

  drag(gripRef)
  drop(rowRef)

  return (
    <div
      ref={rowRef}
      className={`rounded-md border border-gray-200 bg-gray-50/80 p-3 transition-opacity ${
        isDragging ? 'opacity-50' : ''
      }`}
    >
      <Space direction='vertical' className='w-full' size={6}>
        <div className='flex min-w-0 items-center gap-2'>
          <span
            ref={gripRef}
            className='flex h-8 w-6 flex-shrink-0 cursor-grab items-center justify-center text-gray-400 active:cursor-grabbing'
            aria-label='Drag to reorder sort'
          >
            <FontAwesomeIcon icon={faGripVertical} className='text-sm' />
          </span>
          <Select
            placeholder='Select column'
            allowClear
            className='w-full flex-1'
            options={optionsForRow(row.id)}
            value={row.valueKey}
            onChange={(v) => updateRow(row.id, { valueKey: v ?? undefined })}
          />
          {rowCount > 1 && (
            <button
              type='button'
              aria-label='Remove sort'
              className='flex h-8 w-8 flex-shrink-0 items-center justify-center rounded border-0 bg-transparent p-0 text-red-600 hover:bg-red-50'
              onClick={() => removeRow(row.id)}
            >
              <FontAwesomeIcon icon={faTrash} className='text-sm' />
            </button>
          )}
        </div>

        <Radio.Group
          value={row.direction}
          disabled={!row.valueKey}
          onChange={(e) => updateRow(row.id, { direction: e.target.value })}
          className='flex gap-2'
        >
          <Radio value='asc'>Ascending</Radio>
          <Radio value='desc'>Descending</Radio>
        </Radio.Group>
      </Space>
    </div>
  )
}

export function SortPanel({ columns, value, onChange }: SortPanelProps) {
  const sortableColumns = useMemo(() => columns.filter(isSortableColumn), [columns])

  const draftRows = useMemo<SortDraftRow[]>(
    () => (value.length > 0 ? value : [createSortDraftRow()]),
    [value]
  )

  // Dev-only: surface producer bugs. An active sort row's `valueKey` must
  // be a sortable column's `field_name`. Anything else is a producer that
  // emitted a non-canonical identifier (sort_key, field_value_ref, a stale
  // pref from older code, etc.) — the Select can't find it in the options,
  // so the label falls back to the raw key. Stripped from prod builds by
  // dead-code elimination on `process.env.NODE_ENV`.
  useEffect(() => {
    if (process.env.NODE_ENV !== 'development') return
    if (sortableColumns.length === 0) return
    const knownKeys = new Set(sortableColumns.map((c) => c.valueKey))
    for (const row of draftRows) {
      if (row.valueKey && !knownKeys.has(row.valueKey)) {
        console.warn(
          `[SortPanel] active sort row references "${row.valueKey}", which is not a sortable column's field_name. ` +
            `The Select will display the raw key instead of column_title. ` +
            `Check the producer that emitted this key (default-sort constant, column-click handler, or a stored user preference written by older code).`
        )
      }
    }
  }, [draftRows, sortableColumns])

  const updateRow = useCallback((id: string, patch: Partial<SortDraftRow>) => {
    const nextRows = draftRows.map((r) => (r.id === id ? { ...r, ...patch } : r))
    onChange(nextRows)
  }, [draftRows, onChange])

  const addRow = useCallback(() => {
    onChange([...draftRows, createSortDraftRow()])
  }, [draftRows, onChange])

  const removeRow = useCallback((id: string) => {
    const nextRows = draftRows.filter((r) => r.id !== id)
    onChange(nextRows.length > 0 ? nextRows : [createSortDraftRow()])
  }, [draftRows, onChange])

  const moveSortRow = useCallback(
    (dragIndex: number, hoverIndex: number) => {
      const next = [...draftRows]
      const [removed] = next.splice(dragIndex, 1)
      next.splice(hoverIndex, 0, removed)
      onChange(next)
    },
    [draftRows, onChange]
  )

  const optionsForRow = useCallback(
    (rowId: string) => {
      const row = draftRows.find((r) => r.id === rowId)
      const takenByOthers = new Set(
        draftRows
          .filter((r) => r.id !== rowId && r.valueKey)
          .map((r) => r.valueKey)
      )
      return sortableColumns
        .filter((c) => {
          const sk = sortColumnKey(c)
          return !takenByOthers.has(sk) || row?.valueKey === sk
        })
        .map((c) => ({ value: sortColumnKey(c), label: c.columnTitle }))
        .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }))
    },
    [draftRows, sortableColumns]
  )

  return (
    <div className='flex min-h-0 w-full flex-1 flex-col'>
      <div className='flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto pr-1'>
        <DndProvider backend={HTML5Backend}>
          {draftRows.map((row, index) => (
            <DraggableSortRow
              key={row.id}
              row={row}
              index={index}
              rowCount={draftRows.length}
              moveSortRow={moveSortRow}
              optionsForRow={optionsForRow}
              updateRow={updateRow}
              removeRow={removeRow}
            />
          ))}
        </DndProvider>
        <Button
          variant='default'
          className='w-full shrink-0 border border-dashed border-gray-300'
          icon={<FontAwesomeIcon icon={faPlus} />}
          onClick={addRow}
        >
          Add sort
        </Button>
      </div>
    </div>
  )
}
